// Painted world v2 (Oct 15 2026): the gorges carried a little further, for the map's look only. The game's ravine cells
// (terrain code 4) come from a thin noise band, so most gorges are one to three cells long; the reference shows long,
// connected gorges. This extends each gorge from its loose ends by a few cells of cosmetic gorge, deterministically from
// the game's own terrain and the cities — never from what is on screen — so every tile, zoom and query order agrees.
//
// It is decoration: the server's terrain is unchanged and authoritative (movement, the city list, hit targets). An
// extension is anchored to the existing network (it starts from a ravine cell's loose end and continues its direction,
// turning gently), and keeps the same exclusions as everything else painted: never on or beside a city's cell, a lake or
// a mountain, never onto forest... (forest is allowed: the gorge cuts through the woods, whose trees then keep off it).
// The renderer reads the result through `code(x, y)`: 4 for a cosmetic gorge cell, the game's code otherwise — the same
// field the gorge painter, the bridges, the streams' routing and the scenery footing use.
import { cellHash, wrap } from "./geo";

/** Each loose end grows by EXT_MIN–EXT_MAX cells, with this chance. */
export const EXT_MIN = 2;
export const EXT_MAX = 4;
export const EXT_CHANCE = 0.85;
const BLOCK = 16;
/** discovery reaches past the longest extension, so a cell's answer never depends on which blocks were asked for first */
const HALO = EXT_MAX + 2;
const DIRS: readonly (readonly [number, number])[] = [[1, 0], [1, 1], [0, 1], [-1, 1], [-1, 0], [-1, -1], [0, -1], [1, -1]];

export class RavineExtension {
  private cells = new Set<number>();
  private scanned = new Set<number>();
  /** code() answers, per cell (−1: not asked yet) — the painter asks about every cell many times */
  private memo: Int8Array;

  constructor(
    public readonly size: number,
    public readonly seed: number,
    private terrainAt: (x: number, y: number) => number,
    private cityAt: (x: number, y: number) => boolean,
  ) {
    this.memo = new Int8Array(size * size).fill(-1);
  }

  private idx(x: number, y: number): number {
    return wrap(y, this.size) * this.size + wrap(x, this.size);
  }
  private t(x: number, y: number): number {
    return this.terrainAt(wrap(x, this.size), wrap(y, this.size));
  }
  private ravine(x: number, y: number): boolean {
    return this.t(x, y) === 4;
  }
  private hash(x: number, y: number, salt: number): number {
    return cellHash(wrap(x, this.size), wrap(y, this.size), (this.seed ^ salt) >>> 0);
  }

  /** May a cosmetic gorge cell go here? Open ground or forest; no city, lake or mountain on it or around it. */
  private open(x: number, y: number): boolean {
    const t = this.t(x, y);
    if (t !== 0 && t !== 1) return false;
    for (let dy = -1; dy <= 1; dy++) {
      for (let dx = -1; dx <= 1; dx++) {
        if (this.cityAt(wrap(x + dx, this.size), wrap(y + dy, this.size))) return false;
        const n = this.t(x + dx, y + dy);
        if (n === 3 || n === 2) return false;
      }
    }
    // a city two cells off keeps its approach clear on the near side
    for (let dy = -2; dy <= 2; dy++) for (let dx = -2; dx <= 2; dx++) {
      if (Math.max(Math.abs(dx), Math.abs(dy)) === 2 && this.cityAt(wrap(x + dx, this.size), wrap(y + dy, this.size)) && this.hash(x, y, 913) < 0.5) return false;
    }
    return true;
  }

  /** Would a gorge cell here run alongside another gorge (any ravine cell within two cells, other than the loose end it
   *  grows from and that end's own neighbours)? Two gorges side by side would join into ladders of short links. */
  private crowded(x: number, y: number, fromX: number, fromY: number): boolean {
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        const ax = x + dx, ay = y + dy;
        if (!this.ravine(ax, ay)) continue;
        if (Math.max(Math.abs(ax - fromX), Math.abs(ay - fromY)) <= 1) continue;
        return true;
      }
    }
    return false;
  }

  /** Grow from a loose end (x, y) heading `d` (an index into DIRS). */
  private grow(x: number, y: number, d: number, salt: number): void {
    if (this.hash(x, y, 901 + salt) > EXT_CHANCE) return;
    const len = EXT_MIN + Math.floor(this.hash(x, y, 903 + salt) * (EXT_MAX - EXT_MIN + 1));
    let cx = x, cy = y, dir = d;
    for (let k = 0; k < len; k++) {
      const h = this.hash(cx, cy, 907 + salt * 3 + k);
      // mostly straight on, sometimes a 45° bend either way (the order fixed by the hash)
      const turn = h < 0.6 ? [0, 1, -1] : h < 0.8 ? [1, 0, -1] : [-1, 0, 1];
      let moved = false;
      for (const tr of turn) {
        const nd = (dir + tr + 8) % 8;
        const [dx, dy] = DIRS[nd];
        const nx = cx + dx, ny = cy + dy;
        if (this.ravine(nx, ny) || !this.open(nx, ny) || this.crowded(nx, ny, x, y)) continue;
        this.cells.add(this.idx(nx, ny));
        cx = nx; cy = ny; dir = nd;
        moved = true;
        break;
      }
      if (!moved) return;
    }
  }

  private discover(bx: number, by: number): void {
    const n = Math.ceil(this.size / BLOCK);
    const k = by * n + bx;
    if (this.scanned.has(k)) return;
    this.scanned.add(k);
    for (let y = by * BLOCK; y < Math.min(this.size, (by + 1) * BLOCK); y++) {
      for (let x = bx * BLOCK; x < Math.min(this.size, (bx + 1) * BLOCK); x++) {
        if (!this.ravine(x, y)) continue;
        const nb: number[] = [];
        DIRS.forEach(([dx, dy], i) => { if (this.ravine(x + dx, y + dy)) nb.push(i); });
        if (nb.length === 1) {
          // a loose end: carry on away from its one neighbour
          this.grow(x, y, (nb[0] + 4) % 8, 0);
        } else if (nb.length === 0) {
          // a lone cell: a gorge through it, both ways, at an angle of its own
          const d = Math.floor(this.hash(x, y, 911) * 4);
          this.grow(x, y, d, 1);
          this.grow(x, y, d + 4, 2);
        }
      }
    }
  }

  /** Is (x, y) a cosmetic gorge cell? (Unwrapped coordinates welcome.) */
  has(x: number, y: number): boolean {
    const n = Math.ceil(this.size / BLOCK);
    const x0 = Math.floor(wrap(x - HALO, this.size) / BLOCK), y0 = Math.floor(wrap(y - HALO, this.size) / BLOCK);
    const span = Math.ceil((HALO * 2 + 1) / BLOCK) + 1;
    for (let j = 0; j <= span; j++) for (let i = 0; i <= span; i++) this.discover((x0 + i) % n, (y0 + j) % n);
    return this.cells.has(this.idx(x, y));
  }

  /** The terrain code with the cosmetic gorges in: 4 on an extension cell, the game's code everywhere else. */
  code(x: number, y: number): number {
    const k = this.idx(x, y);
    const m = this.memo[k];
    if (m >= 0) return m;
    const t = this.t(x, y);
    const c = t !== 0 && t !== 1 ? t : this.has(x, y) ? 4 : t;
    this.memo[k] = c;
    return c;
  }
}
